import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, AppState, StyleSheet, View } from 'react-native';
import ModalLayout from './ModalLayout';
import Text from '@/components/Typography/Text';
import MessageSpeakAudioQueue from './MessageSpeakAudioQueue';
import Toggle from '@/components/Inputs/Toggle';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useTranslation from '@/hooks/useTranslation';
import { ttsApi } from '@/lib/api';
import { downloadCombinedMedia, downloadMedia } from '@/constants/utils/mediaDownload';
import { useAuthStore, useModalStore, useToastStore } from '@/store';
import { useUIStore } from '@/store/ui';
import { READ_ALOUD_VOICES, getReadAloudProviderVoice, normalizeReadAloudVoice } from '@/constants/readAloudVoices';
import { formatCreditBalance } from '@/lib/formatCredits';

const createStyles = (colors, isXS) => StyleSheet.create({
  content: {
    padding: isXS ? 16 : 20,
    paddingTop: isXS ? 8 : 12,
    gap: 16,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  voicePicker: {
    gap: 8,
  },
  voiceToggle: {
    height: 48,
  },
  playerWrap: {
    minHeight: 100,
    borderRadius: 12,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'hidden',
    paddingVertical: 4,
  },
  meta: {
    lineHeight: 20,
  },
});

const SPEAK_POLL_INTERVAL_MS = 600;
// Must exceed the server-side stale-job budget (10 min) so the server's own
// failure state (with its error code) is what the user sees, not a local timeout.
const SPEAK_MAX_WAIT_MS = 11 * 60 * 1000;
// Tolerate transient network blips while polling before giving up.
const SPEAK_MAX_CONSECUTIVE_POLL_ERRORS = 3;

const MessageSpeakModal = forwardRef(function MessageSpeakModal({ onClose, onStop, text = '', onPlaybackStateChange, suspended = false }, ref) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const fetchUserSlim = useAuthStore(state => state.fetchUserSlim);
  const voice = useUIStore(state => normalizeReadAloudVoice(state.readAloudVoice));
  const setReadAloudVoice = useUIStore(state => state.setReadAloudVoice);

  const [status, setStatus] = useState('loading'); // loading | partial | ready | error
  const [errorMessage, setErrorMessage] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [totalChunks, setTotalChunks] = useState(null);
  const [readyChunks, setReadyChunks] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(null);
  const [cacheHit, setCacheHit] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requestIdRef = useRef(0);
  const playerRef = useRef(null);
  const knownChunksRef = useRef(new Map());
  useImperativeHandle(ref, () => ({
    play: () => playerRef.current?.play?.(),
    pause: () => playerRef.current?.pause?.(),
  }), []);
  useEffect(() => {
    if (status === 'loading' || status === 'error') {
      onPlaybackStateChange?.({ playing: false, position: 0, duration: 0, available: false,
        error: status === 'error' });
    }
  }, [onPlaybackStateChange, status]);

  const trimmedText = useMemo(() => (typeof text === 'string' ? text.trim() : ''), [text]);

  // Callbacks/labels live in a ref so the generation effect below only
  // re-runs for a new text, voice, or explicit retry. It previously depended on
  // t/onClose/addToast/etc., whose identities change across re-renders —
  // each change ran the cleanup, which DELETEd the in-flight job, and the
  // next poll 404'd: a spurious "Failed to generate speech" while the
  // server kept generating (and charging).
  const callbacksRef = useRef({ t, onClose, onStop, openModal, addToast, fetchUserSlim });
  callbacksRef.current = { t, onClose, onStop, openModal, addToast, fetchUserSlim };

  useEffect(() => {
    setErrorMessage('');
    setAudioUrl(null);
    setChunks([]);
    knownChunksRef.current = new Map();
    setTotalChunks(null);
    setReadyChunks(0);
    setCreditsUsed(null);
    setCacheHit(false);
    const { t: translate } = callbacksRef.current;
    if (!trimmedText) {
      setStatus('error');
      setErrorMessage(translate('messages.speak.empty'));
      return undefined;
    }
    const describeError = (error) => {
      const { t: tt } = callbacksRef.current;
      const code = error?.errorCode;
      if (code === 'tts_timeout' || code === 'tts_client_timeout') {
        return tt('messages.speak.timeout');
      }
      if (code === 'tts_provider_error' || code === 'tts_empty_audio') {
        return tt('messages.speak.providerFailed');
      }
      if (code === 'tts_cancelled') {
        return tt('messages.speak.cancelled');
      }
      return error?.message || tt('messages.speak.failed');
    };

    const requestId = ++requestIdRef.current;
    let cancelled = false;
    let generatedConversationId = null;
    // Time spent backgrounded doesn't count against the wait budget — iOS
    // suspends timers, so a wall-clock deadline would expire while the user
    // isn't even looking at the modal.
    let backgroundedSince = null;
    const appStateSubscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') {
        if (backgroundedSince) {
          startedAt += Date.now() - backgroundedSince;
          backgroundedSince = null;
        }
      } else if (!backgroundedSince) {
        backgroundedSince = Date.now();
      }
    });

    const isStale = () => cancelled || requestId !== requestIdRef.current;
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    let startedAt = Date.now();

    (async () => {
      setStatus('loading');
      try {
        const job = await ttsApi.speak({ text: trimmedText, voice: getReadAloudProviderVoice(voice) });
        generatedConversationId = job.conversationId || null;
        if (isStale()) {
          if (generatedConversationId) {
            ttsApi.cleanup(generatedConversationId).catch(() => {});
          }
          return;
        }
        if (Number.isInteger(job.totalChunks)) setTotalChunks(job.totalChunks);
        if (!generatedConversationId) {
          throw new Error(callbacksRef.current.t('messages.speak.failed'));
        }

        let consecutivePollErrors = 0;

        while (!isStale()) {
          if (Date.now() - startedAt > SPEAK_MAX_WAIT_MS) {
            const timeoutError = new Error(callbacksRef.current.t('messages.speak.timeout'));
            timeoutError.errorCode = 'tts_client_timeout';
            throw timeoutError;
          }
          let result;
          try {
            result = await ttsApi.speakStatus(generatedConversationId);
            consecutivePollErrors = 0;
          } catch (pollError) {
            if (pollError?.status === 404 || pollError?.errorCode === 'tts_speak_not_found') {
              throw new Error(callbacksRef.current.t('messages.speak.failed'));
            }
            consecutivePollErrors += 1;
            if (consecutivePollErrors >= SPEAK_MAX_CONSECUTIVE_POLL_ERRORS) {
              throw pollError;
            }
            await sleep(SPEAK_POLL_INTERVAL_MS);
            continue;
          }

          if (isStale()) return;
          const resultChunks = Array.isArray(result?.chunks)
            ? result.chunks.filter(chunk => Number.isInteger(chunk?.index) && chunk.index >= 0 && chunk?.url)
            : [];
          if (resultChunks.length > 0) {
            resultChunks.forEach(chunk => knownChunksRef.current.set(chunk.index, chunk));
            setChunks(Array.from(knownChunksRef.current.values()).sort((a, b) => a.index - b.index));
          }
          const hasChunkZero = knownChunksRef.current.has(0);
          if (Number.isInteger(result?.totalChunks)) setTotalChunks(result.totalChunks);
          setReadyChunks(
            Number.isInteger(result?.readyChunks)
              ? result.readyChunks
              : knownChunksRef.current.size
          );
          if (typeof result?.creditsUsed === 'number') setCreditsUsed(result.creditsUsed);
          if (typeof result?.cacheHit === 'boolean') setCacheHit(result.cacheHit);
          if (result?.url) setAudioUrl(result.url);

          if (hasChunkZero || result?.url) {
            setStatus(result?.status === 'ready' ? 'ready' : 'partial');
          }

          if (result?.status === 'ready' && (hasChunkZero || result?.url)) {
            callbacksRef.current.fetchUserSlim?.().catch(() => {});
            return;
          }

          if (result?.status === 'failed') {
            const failedError = new Error(result.message || callbacksRef.current.t('messages.speak.failed'));
            failedError.errorCode = result.errorCode;
            throw failedError;
          }
          if (result?.status === 'cancelled') {
            const cancelledError = new Error(callbacksRef.current.t('messages.speak.cancelled'));
            cancelledError.errorCode = 'tts_cancelled';
            throw cancelledError;
          }
          // 'pending' or 'partial' — keep polling. Polling remains the
          // transport fallback even when socket progress events are absent.
          await sleep(SPEAK_POLL_INTERVAL_MS);
        }
      } catch (error) {
        if (isStale()) return;
        const { t: tt, onClose: minimize, onStop: stop, openModal: open, addToast: toast } = callbacksRef.current;
        const close = stop || minimize;
        if (error?.errorCode === 'insufficient_credits' || error?.status === 402) {
          close?.();
          open?.('OutOfCreditsModal', {
            title: tt('messages.speak.outOfCreditsTitle'),
            subtitle: tt('messages.speak.outOfCreditsSubtitle'),
            source: 'tts',
          });
          return;
        }
        if (error?.errorCode === 'plus_required' || error?.status === 403) {
          close?.();
          open?.('PlusFeatureModal', { feature: 'read-aloud' });
          return;
        }
        const message = describeError(error);
        setErrorMessage(message);
        setStatus('error');
        toast?.(message, 'error');
      }
    })();

    return () => {
      cancelled = true;
      appStateSubscription?.remove?.();
      if (generatedConversationId) {
        ttsApi.cleanup(generatedConversationId).catch(() => {});
      }
    };
  }, [trimmedText, voice, attempt]);

  const handleVoiceChange = (nextVoice) => {
    if (nextVoice === voice) return;
    playerRef.current?.stop?.();
    setStatus('loading');
    setReadAloudVoice(nextVoice);
  };

  const lowerActions = useMemo(() => {
    const actions = [];
    const chunkUrls = chunks.reduce((urls, chunk) => {
      if (chunk?.url) urls.push(chunk.url);
      return urls;
    }, []);
    if (status === 'ready' && (audioUrl || chunkUrls.length > 0)) {
      actions.push({
        title: isDownloading ? t('messages.speak.downloading') : t('messages.speak.download'),
        disabled: isDownloading,
        onPress: async () => {
          if (isDownloading) return;
          setIsDownloading(true);
          try {
            const result = audioUrl
              ? await downloadMedia(audioUrl, 'gab-ai-read-aloud.mp3')
              : await downloadCombinedMedia(chunkUrls, 'gab-ai-read-aloud.mp3');
            callbacksRef.current.addToast?.(
              result?.success
                ? callbacksRef.current.t('messages.speak.downloaded')
                : (result?.message || callbacksRef.current.t('messages.speak.downloadFailed')),
              result?.success ? 'success' : 'error'
            );
          } catch (error) {
            callbacksRef.current.addToast?.(
              error?.message || callbacksRef.current.t('messages.speak.downloadFailed'),
              'error'
            );
          } finally {
            setIsDownloading(false);
          }
        },
      });
    }
    if (status === 'error') {
      actions.push({
        title: t('messages.speak.retry'),
        primary: true,
        onPress: () => setAttempt(a => a + 1),
      });
    }
    if (onStop) actions.push({ title: t('messages.speak.stop'), onPress: onStop });
    actions.push({
      title: t(onStop ? 'messages.speak.minimize' : 'common.done'),
      primary: status !== 'error',
      onPress: onClose,
    });
    return actions;
  }, [audioUrl, chunks, isDownloading, onClose, onStop, t, status]);

  return (
    <ModalLayout
      title={t('messages.speak.title')}
      onClose={onClose}
      lowerActions={lowerActions}
    >
      <View style={styles.content}>
        <View style={styles.voicePicker}>
          <Text size="s" weight="medium">{t('messages.speak.voice')}</Text>
          <Toggle
            options={READ_ALOUD_VOICES.map(option => ({
              value: option.value,
              label: t(`messages.speak.${option.value}`),
            }))}
            value={voice}
            onChange={handleVoiceChange}
            colors={colors}
            containerStyle={styles.voiceToggle}
            rounded={false}
            stretch
          />
          <Text size="xs" color="tertiary">
            {t('messages.speak.voiceSaved')}
            {voice === 'male' ? ` ${t('messages.speak.maleLanguage')}` : ''}
          </Text>
        </View>
        {(status === 'loading' || status === 'partial') && (
          <View style={styles.statusRow}>
            <ActivityIndicator color={colors.text_color_primary} />
            <Text size="s" color="secondary">
              {status === 'partial'
                ? (
                  totalChunks
                    ? t('messages.speak.generatingRestProgress')
                      .replace('%READY%', String(readyChunks))
                      .replace('%TOTAL%', String(totalChunks))
                    : t('messages.speak.generatingRest')
                )
                : t('messages.speak.generating')}
            </Text>
          </View>
        )}

        {status === 'error' && (
          <Text size="s" color="secondary" style={styles.meta}>
            {errorMessage || t('messages.speak.failed')}
          </Text>
        )}

        {(status === 'partial' || status === 'ready') && (chunks.length > 0 || audioUrl) && (
          <>
            <View
              style={[
                styles.playerWrap,
                { height: chunks.length > 1 && !audioUrl ? 140 : 100 },
              ]}
            >
              <MessageSpeakAudioQueue
                key={`${voice}:${attempt}`}
                ref={playerRef}
                chunks={chunks}
                fallbackUrl={audioUrl}
                isGenerationComplete={status === 'ready'}
                size={320}
                persistentPlayback={!!onStop}
                suspended={suspended}
                onPlaybackStateChange={onPlaybackStateChange}
              />
            </View>
            {typeof creditsUsed === 'number' && creditsUsed > 0 && (
              <Text size="xs" color="tertiary" style={styles.meta}>
                {t('messages.speak.creditsCharged').replace('%COUNT%', formatCreditBalance(creditsUsed))}
              </Text>
            )}
            {cacheHit && (
              <Text size="xs" color="tertiary" style={styles.meta}>
                {t('messages.speak.cacheHit')}
              </Text>
            )}
          </>
        )}
      </View>
    </ModalLayout>
  );
});

MessageSpeakModal.modalOptions = {
  width: 420,
  height: 540,
  heightMobile: [620, '85%'],
  background: 'primary',
};

export default MessageSpeakModal;
